import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowLeft,
  Check,
  Sparkles,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { WHATSAPP_URL } from "../lib/constants";
import { useT } from "../i18n";

const HERO_IMAGE =
  "https://customer-assets-0z36b82j.emergentagent.net/job_movimento-saude-1/artifacts/7yffygfh_Medicina%20Chinesa.jpeg";

const fade = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.75, ease: "easeOut" },
};

export default function MedicinaChinesaPage() {
  const { t } = useT();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const indicationsItems = t("medicinaChinesaPage.indicationsItems");

  return (
    <div className="App bg-[#FEFEFE]" data-testid="medicina-chinesa-page">
      <Navbar />

      {/* ═══════ HERO ═══════ */}
      <section
        data-testid="mtc-hero"
        className="relative min-h-[78vh] lg:min-h-[80vh] w-full overflow-hidden bg-[#0F1A17]"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Medicina Tradicional Chinesa na Yin Motion"
            className="w-full h-full object-cover object-[65%_center] opacity-[0.9]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A17]/75 via-[#0F1A17]/45 to-[#0F1A17]/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A17]/45 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 min-h-[78vh] lg:min-h-[80vh] flex flex-col">
          <div className="flex-1 flex items-end pb-20 md:pb-28 pt-36 md:pt-44">
            <div className="w-full max-w-[1200px] mx-auto px-5 md:px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                className="max-w-[860px]"
              >
                <Link
                  to="/"
                  data-testid="mtc-back"
                  className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase font-semibold text-white/70 hover:text-[#EE8984] transition-colors mb-10"
                >
                  <ArrowLeft size={14} />
                  {t("medicinaChinesaPage.back")}
                </Link>

                <div className="flex items-center gap-3 mb-8">
                  <span className="h-px w-10 bg-[#EE8984]" />
                  <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#F7DEDA]">
                    {t("medicinaChinesaPage.eyebrow")}
                  </span>
                </div>

                <h1 className="font-serif text-[34px] sm:text-[44px] lg:text-[56px] xl:text-[64px] leading-[1.1] tracking-tight text-white font-light">
                  {t("medicinaChinesaPage.heroTitle")}
                </h1>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ INTRO ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#FEFEFE]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
          <motion.div {...fade} className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
                {t("medicinaChinesaPage.introEyebrow")}
              </span>
            </div>
          </motion.div>
          <motion.div {...fade} className="lg:col-span-8">
            <p className="font-serif text-[22px] md:text-[28px] lg:text-[32px] leading-[1.4] tracking-tight text-[#2A322C] font-light">
              {t("medicinaChinesaPage.introBody")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════ COMO FUNCIONA ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#EEF2E9]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div {...fade} className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
                {t("medicinaChinesaPage.howEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[30px] md:text-[38px] lg:text-[44px] leading-[1.15] tracking-tight text-[#2A322C] font-semibold">
              {t("medicinaChinesaPage.howTitle")}
            </h2>
          </motion.div>
          <motion.div {...fade} className="lg:col-span-7">
            <p className="font-serif italic text-[20px] md:text-[24px] lg:text-[26px] leading-[1.55] text-[#5C6660] border-l-2 border-[#A3BD98]/60 pl-7 md:pl-9">
              {t("medicinaChinesaPage.howBody")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════ INDICAÇÕES ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#FEFEFE]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div {...fade} className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#EE8984]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#CF8376]">
                {t("medicinaChinesaPage.indicationsEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[32px] md:text-[40px] lg:text-[46px] leading-[1.15] tracking-tight text-[#2A322C] font-semibold">
              {t("medicinaChinesaPage.indicationsTitle")}
            </h2>
          </motion.div>

          <motion.ul {...fade} className="lg:col-span-7 space-y-4">
            {indicationsItems.map((item, i) => (
              <li
                key={i}
                data-testid={`mtc-indication-${i}`}
                className="group flex items-center gap-5 bg-[#FEFEFE] border border-[#A3BD98]/15 rounded-full pl-6 pr-8 py-5 hover:border-[#A3BD98]/45 hover:translate-x-[4px] transition-all duration-300 shadow-[0_6px_18px_-14px_rgba(42,50,44,0.12)]"
              >
                <span className="shrink-0 w-10 h-10 rounded-full border border-[#A3BD98]/50 flex items-center justify-center text-[#A3BD98] group-hover:bg-[#A3BD98] group-hover:text-white group-hover:border-[#A3BD98] transition-colors duration-300">
                  <Check size={15} strokeWidth={2.5} />
                </span>
                <span className="text-[17px] md:text-[19px] text-[#2A322C] leading-[1.3] font-medium font-serif">
                  {item}
                </span>
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* ═══════ ABORDAGEM COMPLEMENTAR (dark) ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#0F1A17] overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(163,189,152,0.1),transparent_55%)] pointer-events-none"
        />
        <motion.div {...fade} className="relative max-w-[900px] mx-auto px-5 md:px-6 text-center">
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#A3BD98]/70" />
            <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#A3BD98]/80">
              {t("medicinaChinesaPage.complementaryEyebrow")}
            </span>
            <span className="h-px w-10 bg-[#A3BD98]/70" />
          </div>
          <h2 className="font-serif text-[32px] md:text-[42px] lg:text-[52px] leading-[1.15] tracking-tight text-white font-semibold">
            {t("medicinaChinesaPage.complementaryTitle")}
          </h2>
          <p className="mt-8 text-[17px] md:text-[19px] text-white/75 leading-[1.85] font-light max-w-2xl mx-auto">
            {t("medicinaChinesaPage.complementaryBody")}
          </p>
        </motion.div>
      </section>

      {/* ═══════ OBJETIVO ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#EEF2E9]">
        <motion.div {...fade} className="max-w-[900px] mx-auto px-5 md:px-6 text-center">
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#A3BD98]" />
            <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
              {t("medicinaChinesaPage.goalEyebrow")}
            </span>
            <span className="h-px w-10 bg-[#A3BD98]" />
          </div>
          <Sparkles size={28} strokeWidth={1.5} className="text-[#A3BD98] mx-auto mb-6" />
          <h2 className="font-serif text-[28px] md:text-[36px] lg:text-[42px] leading-[1.25] tracking-tight text-[#2A322C] font-semibold">
            {t("medicinaChinesaPage.goalTitle")}
          </h2>
          <p className="mt-7 text-[17px] md:text-[19px] text-[#5C6660] leading-[1.85] font-light max-w-2xl mx-auto">
            {t("medicinaChinesaPage.goalBody")}
          </p>
        </motion.div>
      </section>

      {/* ═══════ CTA FINAL ═══════ */}
      <section className="relative py-24 md:py-32 bg-[#FEFEFE] border-t border-[#E4E9DE]">
        <motion.div {...fade} className="max-w-[900px] mx-auto px-5 md:px-6 text-center">
          <p className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD] mb-6">
            {t("medicinaChinesaPage.ctaEyebrow")}
          </p>
          <h3 className="font-serif text-[26px] md:text-[34px] lg:text-[40px] leading-[1.25] tracking-tight text-[#2A322C] font-light max-w-2xl mx-auto">
            {t("medicinaChinesaPage.ctaTitle")}
          </h3>
          <div className="mt-10 flex items-center justify-center">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mtc-cta"
              className="group inline-flex items-center gap-2 bg-[#EE8984] text-white rounded-full px-9 py-[18px] text-[14px] font-semibold tracking-wide hover:bg-[#CF8376] hover:scale-[1.02] hover:shadow-[0_16px_40px_-12px_rgba(238,137,132,0.55)] transition-all duration-300"
            >
              {t("medicinaChinesaPage.ctaButton")}
              <ArrowUpRight
                size={17}
                className="group-hover:rotate-45 transition-transform duration-300"
              />
            </a>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
